import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line } from 'recharts';
import { MesComparativo } from '@/utils/financialUtils';
import { formatCurrency } from '@/utils/formatCurrency';

interface FluxoCaixaComparativoProps {
  dados: MesComparativo[];
  isLoading?: boolean;
}

export default function FluxoCaixaComparativo({ dados, isLoading }: FluxoCaixaComparativoProps) {
  const dadosFormatados = useMemo(() => {
    return dados.map(item => ({
      ...item,
      mes: item.mes,
      receitas: parseFloat(item.receitas.toFixed(2)),
      despesas: parseFloat(item.despesas.toFixed(2)),
      saldo: parseFloat(item.saldo.toFixed(2)),
    }));
  }, [dados]);

  const estatisticas = useMemo(() => {
    if (dadosFormatados.length === 0) {
      return {
        totalReceitas: 0,
        totalDespesas: 0,
        saldoTotal: 0,
        maiorReceita: { mes: '', valor: 0 },
        maiorDespesa: { mes: '', valor: 0 },
        crescimento: 0,
      };
    }

    const totalReceitas = dadosFormatados.reduce((acc, item) => acc + item.receitas, 0);
    const totalDespesas = dadosFormatados.reduce((acc, item) => acc + item.despesas, 0);
    const saldoTotal = totalReceitas - totalDespesas;

    const maiorReceita = dadosFormatados.reduce((max, item) =>
      item.receitas > max.valor ? { mes: item.mes, valor: item.receitas } : max,
      { mes: '', valor: 0 }
    );

    const maiorDespesa = dadosFormatados.reduce((max, item) =>
      item.despesas > max.valor ? { mes: item.mes, valor: item.despesas } : max,
      { mes: '', valor: 0 }
    );

    let crescimento = 0;
    if (dadosFormatados.length > 1) {
      const mesAtual = dadosFormatados[dadosFormatados.length - 1];
      const mesPosterior = dadosFormatados[dadosFormatados.length - 2];
      const receitasMesAtual = mesAtual.receitas;
      const receitasMesPosterior = mesPosterior.receitas;

      if (receitasMesPosterior !== 0) {
        crescimento = ((receitasMesAtual - receitasMesPosterior) / receitasMesPosterior) * 100;
      }
    }

    return {
      totalReceitas,
      totalDespesas,
      saldoTotal,
      maiorReceita,
      maiorDespesa,
      crescimento,
    };
  }, [dadosFormatados]);

  if (isLoading) {
    return (
      <div className="w-full h-96 flex items-center justify-center text-muted-foreground">
        Carregando gráfico...
      </div>
    );
  }

  if (dadosFormatados.length === 0) {
    return (
      <div className="w-full h-96 flex items-center justify-center text-muted-foreground">
        Sem dados para exibir
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900">{data.mes}</p>
          <p className="text-sm text-green-600">Receitas: {formatCurrency({ value: data.receitas, currency: 'BRL' })}</p>
          <p className="text-sm text-red-600">Despesas: {formatCurrency({ value: data.despesas, currency: 'BRL' })}</p>
          <p className="text-sm text-purple-600 font-semibold">Saldo: {formatCurrency({ value: data.saldo, currency: 'BRL' })}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="w-full h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={dadosFormatados} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="mes"
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="square"
              formatter={(value) => {
                const labels: Record<string, string> = {
                  receitas: 'Receitas',
                  despesas: 'Despesas',
                  saldo: 'Saldo Líquido',
                };
                return labels[value] || value;
              }}
            />
            <Bar dataKey="receitas" fill="#10b981" radius={[8, 8, 0, 0]} name="receitas" />
            <Bar dataKey="despesas" fill="#ef4444" radius={[8, 8, 0, 0]} name="despesas" />
            <Line
              type="monotone"
              dataKey="saldo"
              stroke="#a855f7"
              strokeWidth={3}
              dot={{ fill: '#a855f7', r: 5 }}
              activeDot={{ r: 7 }}
              name="saldo"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Cartões de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-xs text-muted-foreground mb-1">Total de Receitas</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency({ value: estatisticas.totalReceitas, currency: 'BRL' })}</p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-xs text-muted-foreground mb-1">Total de Despesas</p>
          <p className="text-xl font-bold text-red-600">{formatCurrency({ value: estatisticas.totalDespesas, currency: 'BRL' })}</p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-xs text-muted-foreground mb-1">Crescimento</p>
          <p className={`text-xl font-bold ${estatisticas.crescimento >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {estatisticas.crescimento.toFixed(1)}%
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-xs text-muted-foreground mb-1">Saldo Total</p>
          <p className={`text-xl font-bold ${estatisticas.saldoTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency({ value: estatisticas.saldoTotal, currency: 'BRL' })}
          </p>
        </div>
      </div>

      {/* Informações Detalhadas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-green-100/50 p-4 rounded-lg border border-green-200">
          <p className="text-sm font-semibold text-green-900 mb-1">Maior Receita</p>
          <p className="text-lg font-bold text-green-700">{formatCurrency({ value: estatisticas.maiorReceita.valor, currency: 'BRL' })}</p>
          <p className="text-xs text-green-600 mt-1">{estatisticas.maiorReceita.mes}</p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100/50 p-4 rounded-lg border border-red-200">
          <p className="text-sm font-semibold text-red-900 mb-1">Maior Despesa</p>
          <p className="text-lg font-bold text-red-700">{formatCurrency({ value: estatisticas.maiorDespesa.valor, currency: 'BRL' })}</p>
          <p className="text-xs text-red-600 mt-1">{estatisticas.maiorDespesa.mes}</p>
        </div>
      </div>
    </div>
  );
}
